<template>

    <div class="login-template">
        <div class="left-side register">
            <div class="top">
                <a href="/" class="logo">
                    <logo></logo>
                </a>
            </div>
            <div class="login-content">
                <div class="content">
                    <h1 v-if="registerAs == 'screening'">
                        {{ $t('tenant.screening-register.headline') }}
                    </h1>
                    <h1 v-else-if="registerAs == 'contract'">
                        {{ $t('tenant.contract-register.headline') }}
                    </h1>
                    <h1 v-else>
                        {{ $t('register.label.headline') }}
                    </h1>
                    <div v-if="!allowRegistration">
                        ...
                    </div>

                    <div v-if="isInvitationExpired && allowRegistration">
                        <h1>{{ $t('login.form.invitation-expired') }}</h1>
                    </div>
                    <form v-if="!isInvitationExpired && allowRegistration" autocomplete="off">
                        <div class="material-input">
                            <input id="email"
                                   type="email"
                                   v-model="form.email"
                                   class="form-control"
                                   :readonly="isUsingInvitation"
                                   name="email"
                                   required autofocus
                                   v-on="checkEmail"
                                   v-bind:class="{ active: isActiveEmail }"
                                   autofill="off"
                                   autocomplete="off"
                            >
                            <p class="label"><label for="email">
                                {{ $t('login.form.email') }}
                            </label></p>
                        </div>
                        <div class="material-input">
                            <input id="password"
                                   v-model="form.password"
                                   type="password"
                                   class="form-control"
                                   name="password"
                                   required
                                   v-on="checkPassword"
                                   v-bind:class="{ active: isActivePassword }"
                                   autofill="off"
                                   autocomplete="off"
                            >
                            <p class="label"><label for="password">{{ $t('login.form.password') }}</label></p>
                        </div>
                        <div class="material-input">
                            <input id="password-confirm"
                                   type="password"
                                   class="form-control"
                                   name="password_confirmation"
                                   v-model="form.password_confirmation"
                                   required
                                   v-on="checkPasswordTwo"
                                   v-bind:class="{ active: isActivePasswordTwo }"
                                   autofill="off"
                                   autocomplete="off"
                            >
                            <p class="label"><label for="password">{{ $t('login.form.password-repeat') }}</label></p>
                        </div>
                        <label class="custom-checkbox">
                            <input type="checkbox" v-model="acceptTerms">
                            <span class="checkbox">
                                <svg xmlns="http://www.w3.org/2000/svg" width="9.895" height="7.38"
                                     viewBox="0 0 19.79 14.76">
                                    <path class="fill-checkmark"
                                          d="M1330.31,1086.51l-10.8,10.8a2.256,2.256,0,0,1-3.02,0l-4.8-4.8a1.994,1.994,0,1,1,2.82-2.82l3.49,3.48,9.49-9.48A1.994,1.994,0,1,1,1330.31,1086.51Z"
                                          transform="translate(-1311.12 -1083.12)"/>
                                </svg>
                            </span>
                            <div class="text"><p>{{ $t('register.accept-terms.text') }} </p><a target="_blank"
                                                                                               href="https://www.rently.dk/terms/">
                                {{ $t('register.accept-terms.link') }}</a></div>
                        </label>


                        <div class="button-container">
                            <button type="submit" :disabled="!validateInput || registering || !acceptTerms"
                                    class="purple" @click.prevent="register" @click="registerMail">
                                <span v-if="!registering">{{ $t('button.label.register') }}</span>
                                <span v-if="registering">{{ $t('button.label.registering') }}</span>
                            </button>
                            <div>
                                <p>{{ $t('existing-account.label.text') }}</p>
                                <a href="https://app.rently.dk/login">{{ $t('existing-account.label.login') }}</a>
                            </div>
                        </div>
                        <div v-if="showErrors">
                            <ul v-for="error in showErrors">
                                <li>{{ error }}</li>
                            </ul>
                        </div>
                    </form>
                </div>
            </div>
            <div class="bottom">
                <copyright></copyright>
                <span class="seperator">|</span>
                <a target="_blank" href="https://www.rently.dk/terms/">{{ $t('login.label.terms-conditions') }}</a>
            </div>
        </div>

        <!-- If register with screening invitation -->
        <div class="right-side screening-register" v-if="registerAs == 'screening'">
            <div class="content">
                <div class="text">
                    <h2>{{ $t('tenant.screening-register.teaser.headline') }}</h2>
                    <ul>
                        <li>
                            <simple-svg
                                :filepath="'images/SVG/CheckmarkCircle.svg'"
                                :width="'20px'"
                                :height="'20px'"
                            />
                            <span>{{ $t('tenant.screening-register.teaser.reason-one') }}</span>
                        </li>
                        <li>
                            <simple-svg
                                :filepath="'images/SVG/CheckmarkCircle.svg'"
                                :width="'20px'"
                                :height="'20px'"
                            />
                            <span>{{ $t('tenant.screening-register.teaser.reason-two') }}</span>
                        </li>
                        <li>
                            <simple-svg
                                :filepath="'images/SVG/CheckmarkCircle.svg'"
                                :width="'20px'"
                                :height="'20px'"
                            />
                            <span>{{ $t('tenant.screening-register.teaser.reason-three') }}</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        <!-- If register with contract invitation -->
        <div class="right-side contract-register" v-else-if="registerAs == 'contract'">
            <div class="content">
                <div class="text">
                    <h2>{{ $t('tenant.contract-register.teaser.headline') }}</h2>
                    <ul>
                        <li>
                            <simple-svg
                                :filepath="'images/SVG/CheckmarkCircle.svg'"
                                :width="'20px'"
                                :height="'20px'"
                            />
                            <span>{{ $t('tenant.contract-register.teaser.reason-one') }}</span>
                        </li>
                        <li>
                            <simple-svg
                                :filepath="'images/SVG/CheckmarkCircle.svg'"
                                :width="'20px'"
                                :height="'20px'"
                            />
                            <span>{{ $t('tenant.contract-register.teaser.reason-two') }}</span>
                        </li>
                        <li>
                            <simple-svg
                                :filepath="'images/SVG/CheckmarkCircle.svg'"
                                :width="'20px'"
                                :height="'20px'"
                            />
                            <span>{{ $t('tenant.contract-register.teaser.reason-three') }}</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        <!-- Else -->
        <div class="right-side" v-else>
            <div class="content">
                <div class="image">
                    <img src="/images/img/login-illustrationv2.png">
                </div>
                <div class="text">
                    <h2>{{ $t('login.teaser.line-one') }}<br/>{{ $t('login.teaser.line-two') }}</h2>
                </div>
            </div>

        </div>
    </div>

</template>


<script>


import Form from 'vform';
import {mapGetters} from 'vuex';
import {store} from "../../store";
import Logo from '../../components/Logo';
import Copyright from '../../components/Copyright';

export default {
    async beforeRouteEnter(to, from, next) {
        var self = this;
        if (store.getters.getToken) {
            next('/');
        }
        if (JSON.stringify(to.params).length > 2) {
            await store.dispatch('fetchEmailByHash', to.params.hash);
            next();
        } else {
            await store.commit('setEmail', '');
            next();
        }
    },
    data: () => ({
        registering: false,
        acceptTerms: false,
        isActiveEmail: false,
        isActivePassword: false,
        isActivePasswordTwo: false,
        form: new Form({
            email: '',
            password: '',
            password_confirmation: '',
            user_role: 1
        }),
        errors: []
    }),
    mounted() {
        if (this.$route.params.hash || this.$route.query.account == 'tenant') {
            this.form.user_role = 2;
            console.log('changed');
        }
    },
    created() {

        this.$store.commit('setUsingInvitation', this.$route.params.hash || this.$route.query.email ? true : false);
        if (this.$route.query.email) {
            this.form.email = this.$route.query.email;
        } else {
            this.form.email = this.getEmail;
        }

        if (this.$route.query.preselectplan) {
            this.$store.commit('setPreselectedPlanId', this.$route.query.preselectplan);
        }
    },
    computed: {
        registerAs() {
            if (this.$route.query.register) {
                return this.$route.query.register;
            }

            return 'landlord';
        },
        allowRegistration() {
            if (this.$route.params.hash) {
                // We don't need to account for expiration here, as it's dealt with later
                return (this.isInvitationFound);
            } else {
                return true;
            }
        },
        validateInput() {
            return this.validEmail(this.form.email) && this.form.password && (this.form.password === this.form.password_confirmation);
        },
        checkEmail() {
            if (this.form.email === '' || this.form.email === null || this.form.email.value === 0) {
                this.isActiveEmail = false;
            } else {
                this.isActiveEmail = true;
            }
        },
        checkPassword() {
            if (this.form.password === '' || this.form.password === null || this.form.password.value === 0) {
                this.isActivePassword = false;
            } else {
                this.isActivePassword = true;
            }
        },
        checkPasswordTwo() {
            if (this.form.password_confirmation === '' || this.form.password_confirmation === null || this.form.password_confirmation.value === 0) {
                this.isActivePasswordTwo = false;
            } else {
                this.isActivePasswordTwo = true;
            }
        },
        showErrors() {
            this.registering = false;
            return this.errors
        },
        ...mapGetters([
            'getEmail',
            'isUsingInvitation',
            'isInvitationExpired',
            'isInvitationFound',
        ])
    },
    components: {
        Logo,
        Copyright
    },
    methods: {
        validEmail(email) {
            let re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            return re.test(email);
        },
        registerMail: function (event) {
            $('#mc-embedded-subscribe').click();
        },
        async register() {
            
            this.registering = true;
            const {data} = await this.form.post(window.config.apiUrl + 'api/register');

            if (data.is_registered_by_invite === false && this.$gtag) {
                this.$gtag.event('Landlord Signup', {
                    eventCategory: 'Signup',
                    eventLabel: 'New Landlord Signup',
                    eventValue: 600
                });
            }
            this.$store.commit('setToken', data.token);
            this.$store.dispatch('login', data);
            if (window.fbq) {
                window.fbq('track', 'CompleteRegistration');
            }
            if (this.$route.query.next) {
                this.$router.push(this.$route.query.next);
            } else {
                this.$router.push('/');
            }

        }
    }
}
</script>

<style scoped>

</style>
